import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, checkAndRedirectIfAuthenticated } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  // Check auth status once on mount
  useEffect(() => {
    // Immediately render content if we know we're authenticated
    if (isAuthenticated && !loading) {
      setChecking(false);
      return;
    }
    
    // If authentication status is unknown or we know we're not authenticated,
    // verify once then cache the result
    const verifyAuth = async () => {
      try {
        // This call to checkAndRedirectIfAuthenticated will automatically redirect
        // to login if not authenticated, so we don't need a separate redirect
        await checkAndRedirectIfAuthenticated(false);
        setChecking(false);
      } catch (error) {
        console.error('Error verifying authentication:', error);
        setChecking(false);
      }
    };

    verifyAuth();
    // We only want to check once on mount - dependency array should be empty
  }, []); 

  // Show loading spinner while checking authentication status
  if (loading || checking) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 size={36} className="animate-spin text-blue-600" />
      </div>
    );
  }

  // If we're not authenticated after checking, redirect to login
  // This handles the case where checkAndRedirectIfAuthenticated didn't redirect
  if (!isAuthenticated) {
    // Save the current location the user was trying to go to
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;